/**
 * New Relic Memory & Performance Instrumentation
 * Sends JS heap metrics, long tasks, and navigation timing to New Relic as PageAction events
 * Falls back to console logging when New Relic is not available (local dev)
 *
 * Usage: Call startNewRelicMemoryTracking() once on app init
 */

interface MemoryPerformance extends Performance {
  memory?: {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  };
}

interface NewRelicWindow extends Window {
  newrelic?: {
    addPageAction: (name: string, attributes: Record<string, unknown>) => void;
    setCustomAttribute: (name: string, value: string | number) => void;
  };
}

interface MemorySnapshot {
  usedHeapMB: number | null;
  totalHeapMB: number | null;
  heapLimitMB: number | null;
  heapUsagePercent: number | null;
  hasMemoryAPI: boolean;
  domNodes: number;
  longTaskCount: number;
  longTaskTotalDurationMs: number;
  ttfbMs: number | null;
  domCompleteMs: number | null;
  loadTimeMs: number | null;
  sessionDurationSec: number;
  pathname: string;
  [key: string]: unknown;
}

// Track long tasks between snapshots
let longTaskCount = 0;
let longTaskTotalDuration = 0;
let longTaskObserver: PerformanceObserver | null = null;

// Track session start time
let sessionStartTime: number | null = null;

/**
 * Initialize long task observer
 * Long tasks are tasks that take > 50ms and block the main thread
 */
export function initLongTaskObserver(): () => void {
  if (
    typeof window === "undefined" ||
    typeof PerformanceObserver === "undefined"
  ) {
    return () => {};
  }

  try {
    longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTaskCount++;
        longTaskTotalDuration += entry.duration;
      }
    });

    longTaskObserver.observe({ entryTypes: ["longtask"] });
  } catch {
    // Long task observer not supported
    console.warn("[Memory Tracking] Long task observer not supported");
  }

  return () => {
    longTaskObserver?.disconnect();
    longTaskObserver = null;
  };
}

/**
 * Get navigation timing metrics (only available after page load)
 */
function getNavigationTiming(): {
  ttfbMs: number | null;
  domCompleteMs: number | null;
  loadTimeMs: number | null;
} {
  if (typeof window === "undefined") {
    return { ttfbMs: null, domCompleteMs: null, loadTimeMs: null };
  }

  try {
    const navEntries = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    const navTiming = navEntries[0];

    if (!navTiming) {
      return { ttfbMs: null, domCompleteMs: null, loadTimeMs: null };
    }

    return {
      ttfbMs: Math.round(navTiming.responseStart - navTiming.requestStart),
      domCompleteMs: Math.round(navTiming.domComplete - navTiming.startTime),
      loadTimeMs: Math.round(navTiming.loadEventEnd - navTiming.startTime),
    };
  } catch {
    return { ttfbMs: null, domCompleteMs: null, loadTimeMs: null };
  }
}

/**
 * Get session duration in seconds
 */
function getSessionDuration(): number {
  if (!sessionStartTime) {
    sessionStartTime = Date.now();
    return 0;
  }
  return Math.round((Date.now() - sessionStartTime) / 1000);
}

/**
 * Reset long task counters (called after each snapshot)
 */
function resetLongTaskCounters(): void {
  longTaskCount = 0;
  longTaskTotalDuration = 0;
}

/**
 * Collect memory snapshot data
 */
function collectMemorySnapshot(): MemorySnapshot {
  const perf = performance as MemoryPerformance;

  // Memory metrics (Chrome only)
  const memory = perf.memory;
  const hasMemoryAPI = !!memory;
  const usedHeapMB = memory
    ? Math.round(memory.usedJSHeapSize / 1024 / 1024)
    : null;
  const totalHeapMB = memory
    ? Math.round(memory.totalJSHeapSize / 1024 / 1024)
    : null;
  const heapLimitMB = memory
    ? Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
    : null;
  const heapUsagePercent = memory
    ? Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
    : null;

  // DOM metrics
  const domNodes = document.getElementsByTagName("*").length;

  // Navigation timing (captured once per page)
  const navTiming = getNavigationTiming();

  // Session duration
  const sessionDurationSec = getSessionDuration();

  // Capture current long task metrics before reset
  const currentLongTaskCount = longTaskCount;
  const currentLongTaskDuration = Math.round(longTaskTotalDuration);

  return {
    usedHeapMB,
    totalHeapMB,
    heapLimitMB,
    heapUsagePercent,
    hasMemoryAPI,
    domNodes,
    longTaskCount: currentLongTaskCount,
    longTaskTotalDurationMs: currentLongTaskDuration,
    ttfbMs: navTiming.ttfbMs,
    domCompleteMs: navTiming.domCompleteMs,
    loadTimeMs: navTiming.loadTimeMs,
    sessionDurationSec,
    pathname: window.location.pathname,
  };
}

/**
 * Log memory snapshot to console (fallback for local dev)
 */
function logMemoryToConsole(snapshot: MemorySnapshot): void {
  const heapInfo = snapshot.hasMemoryAPI
    ? `${snapshot.usedHeapMB}MB / ${snapshot.heapLimitMB}MB (${snapshot.heapUsagePercent}%)`
    : "N/A (Chrome only)";

  console.log(
    `%c[Memory Snapshot]%c ${snapshot.pathname} | Heap: ${heapInfo} | DOM: ${snapshot.domNodes} | Long tasks: ${snapshot.longTaskCount} (${snapshot.longTaskTotalDurationMs}ms) | Session: ${snapshot.sessionDurationSec}s`,
    "color: #00d9ff; font-weight: bold",
    "color: inherit",
  );

  // Log detailed data in collapsed group
  console.groupCollapsed("[Memory Snapshot] Full data");
  console.table({
    "Heap Used (MB)": snapshot.usedHeapMB,
    "Heap Total (MB)": snapshot.totalHeapMB,
    "Heap Limit (MB)": snapshot.heapLimitMB,
    "Heap Usage (%)": snapshot.heapUsagePercent,
    "DOM Nodes": snapshot.domNodes,
    "Long Tasks": snapshot.longTaskCount,
    "Long Task Duration (ms)": snapshot.longTaskTotalDurationMs,
    "TTFB (ms)": snapshot.ttfbMs,
    "DOM Complete (ms)": snapshot.domCompleteMs,
    "Load Time (ms)": snapshot.loadTimeMs,
    "Session Duration (s)": snapshot.sessionDurationSec,
    Path: snapshot.pathname,
  });
  console.groupEnd();
}

/**
 * Send a single memory snapshot to New Relic (or console if NR not available)
 * Includes memory, DOM nodes, long tasks, and navigation timing
 */
export function sendMemorySnapshot(): void {
  if (typeof window === "undefined") return;

  const win = window as NewRelicWindow;
  const snapshot = collectMemorySnapshot();

  if (win.newrelic) {
    // Send to New Relic
    win.newrelic.addPageAction("MemorySnapshot", snapshot);
  } else {
    // Fallback to console logging (local dev or NR not loaded yet)
    logMemoryToConsole(snapshot);
  }

  // Reset long task counters for next interval
  resetLongTaskCounters();
}

/**
 * @deprecated Use sendMemorySnapshot instead
 */
export const sendMemoryToNewRelic = sendMemorySnapshot;

/**
 * Start periodic memory tracking
 * Sends data to New Relic if available, otherwise logs to console
 *
 * @param intervalMs - Interval between snapshots in milliseconds (default 60000)
 * @returns cleanup function to stop tracking
 */
export function startNewRelicMemoryTracking(intervalMs = 60000): () => void {
  if (typeof window === "undefined") return () => {};

  const win = window as NewRelicWindow;

  // Log initial status (NR may load later due to async script)
  if (!win.newrelic) {
    console.log(
      "[Memory Tracking] Starting... (New Relic will be used when available)",
    );
  }

  // Initialize session start time
  sessionStartTime = Date.now();

  // Initialize long task observer
  const cleanupLongTaskObserver = initLongTaskObserver();

  // Send initial snapshot after a short delay (allow page to settle)
  const initialTimeout = setTimeout(() => {
    sendMemorySnapshot();
  }, 5000);

  // Set up periodic tracking
  const intervalId = setInterval(() => {
    sendMemorySnapshot();
  }, intervalMs);

  // Also send on page visibility change (when user returns to tab)
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      sendMemorySnapshot();
    }
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Return cleanup function
  return () => {
    clearTimeout(initialTimeout);
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    cleanupLongTaskObserver();
  };
}
